/**
 * InfoTooltip – reusable (i) icon that shows content.
 * Desktop (hover-capable): show on hover. Mobile (touch): show on tap (modal).
 * Use on any screen; tooltip is portal-rendered on web so it is not clipped by overflow.
 * Themed for light and dark mode (readable on both).
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  Pressable,
  Platform,
  useWindowDimensions,
  type ViewProps,
} from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../constants/animations';
import { Info } from 'lucide-react-native';
import { useTheme } from '../../../theme/useTheme';
import { useThemeContext } from '../../../theme/ThemeContext';
import {
  getInfoTooltipStyles,
  getTooltipPortalPosition,
  TOOLTIP_NARROW_BREAKPOINT,
} from './InfoTooltip.styles';

/** True when primary input can hover (desktop/laptop); false on touch-first devices (mobile web, native). */
function useHasHover(): boolean {
  const [hasHover, setHasHover] = useState(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
    return window.matchMedia('(hover: hover)').matches;
  });
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const m = window.matchMedia('(hover: hover)');
    const listener = () => setHasHover(m.matches);
    m.addEventListener('change', listener);
    return () => m.removeEventListener('change', listener);
  }, []);
  return hasHover;
}

export interface InfoTooltipProps {
  /** Content shown in tooltip (string or custom node). */
  content: React.ReactNode;
  /** Smaller icon when true (only when no custom trigger). */
  compact?: boolean;
  /** Accessibility label for the trigger button. */
  accessibilityLabel?: string;
  /** When provided, this element is the hover/press target for the tooltip (no (i) icon shown). */
  trigger?: React.ReactNode;
  /** When true and using trigger, tooltip only shows on hover (desktop); tap goes to the trigger (e.g. button action). */
  tooltipOnlyOnHover?: boolean;
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  content,
  compact = false,
  accessibilityLabel = 'More info',
  trigger,
  tooltipOnlyOnHover = false,
}) => {
  const theme = useTheme();
  const { isDark } = useThemeContext();
  const { width: windowWidth } = useWindowDimensions();
  const narrow = windowWidth < TOOLTIP_NARROW_BREAKPOINT;
  const styles = getInfoTooltipStyles(theme, { narrow, isDark });
  const buttonRef = useRef<View>(null);
  const useCustomTrigger = trigger != null;
  const tooltipMaxWidth = narrow ? 220 : 280;
  const [hover, setHover] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [tooltipRect, setTooltipRect] = useState<{ left: number; top: number } | null>(null);

  const isWeb = Platform.OS === 'web';
  const hasHover = useHasHover();
  const useHoverTrigger = isWeb && hasHover;
  const showTooltip = useHoverTrigger ? hover : modalVisible;

  const showPositionedTooltip = showTooltip && (useHoverTrigger || (isWeb && modalVisible));

  const estimatedHeight =
    typeof content === 'string' && content.length > 90
      ? (narrow ? 100 : 120)
      : (narrow ? 60 : 80);

  useEffect(() => {
    if (!showPositionedTooltip) {
      setTooltipRect(null);
      return;
    }
    const el = buttonRef.current as unknown as HTMLElement | null;
    if (!el?.getBoundingClientRect || typeof window === 'undefined') {
      return;
    }
    const rafId = requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      setTooltipRect(
        getTooltipPortalPosition(
          { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom },
          window.innerWidth,
          window.innerHeight,
          tooltipMaxWidth,
          estimatedHeight
        )
      );
    });
    return () => cancelAnimationFrame(rafId);
  }, [showPositionedTooltip, tooltipMaxWidth, estimatedHeight]);

  // Close on scroll so trigger stays usable (hover: close hover state; touch: close modal so backdrop doesn’t block re-tap).
  useEffect(() => {
    if (!useHoverTrigger || !hover) return;
    const onScroll = () => setHover(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [useHoverTrigger, hover]);

  useEffect(() => {
    if (!isWeb || useHoverTrigger || !modalVisible) return;
    const onScroll = () => requestAnimationFrame(() => setModalVisible(false));
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [isWeb, useHoverTrigger, modalVisible]);

  const handlePress = useCallback(() => {
    if (useHoverTrigger) return;
    if (useCustomTrigger && tooltipOnlyOnHover) return;
    setModalVisible(true);
  }, [useHoverTrigger, useCustomTrigger, tooltipOnlyOnHover]);

  // Defer close so the touch/scroll that triggered close fully ends before we unmount.
  // This avoids the trigger (i) not responding to the next tap after closing (e.g. after scroll).
  const handleCloseModal = useCallback(() => {
    requestAnimationFrame(() => setModalVisible(false));
  }, []);

  const iconSize = compact ? 16 : 20;

  const portalTooltip =
    isWeb &&
    showPositionedTooltip &&
    tooltipRect &&
    typeof document !== 'undefined'
      ? (() => {
          const ReactDOM = require('react-dom');
          const bubble = (
            <View style={[styles.tooltipPortal, tooltipRect]}>
              <Pressable
                style={styles.tooltipBubble}
                onPress={handleCloseModal}
                accessibilityLabel="Close tooltip"
              >
                {typeof content === 'string' ? (
                  <Text style={styles.tooltipText}>{content}</Text>
                ) : (
                  content
                )}
              </Pressable>
              <View style={styles.tooltipPointer} />
            </View>
          );
          const overlay = !useHoverTrigger && (
            <Pressable
              style={styles.tooltipBackdrop}
              onPress={handleCloseModal}
              accessibilityLabel="Close tooltip"
            />
          );
          return ReactDOM.createPortal(
            <>
              {overlay}
              {bubble}
            </>,
            document.body
          );
        })()
      : null;

  const hoverProps =
    Platform.OS === 'web' && useHoverTrigger
      ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as Partial<ViewProps>)
      : {};

  return (
    <>
      {useCustomTrigger ? (
        <View
          ref={buttonRef}
          style={styles.triggerWrapper}
          collapsable={false}
          {...hoverProps}
        >
          <Pressable
            onPress={handlePress}
            style={styles.triggerTouchable}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel}
          >
            {trigger}
          </Pressable>
        </View>
      ) : (
        <View style={styles.wrapper} {...hoverProps}>
          <Pressable
            ref={buttonRef}
            style={({ pressed, hovered }) => [
              styles.iconButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.iconButtonPressed,
              hovered && Platform.OS === 'web' && styles.iconButtonHover,
            ]}
            onPress={handlePress}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel}
          >
            <Info
              size={iconSize}
              color={theme.gray[500]}
            />
          </Pressable>
        </View>
      )}
      {portalTooltip}
      {!useHoverTrigger && !isWeb && (
        <Modal
          visible={modalVisible}
          transparent
          animationType="fade"
          onRequestClose={handleCloseModal}
        >
          <View
            style={styles.modalOverlay}
            onTouchMove={handleCloseModal}
            onStartShouldSetResponder={() => true}
          >
            <Pressable style={styles.modalOverlayTouchable} onPress={handleCloseModal}>
              <Pressable style={styles.modalContent} onPress={handleCloseModal}>
                {typeof content === 'string' ? (
                  <Text style={styles.modalText}>{content}</Text>
                ) : (
                  content
                )}
              </Pressable>
            </Pressable>
          </View>
        </Modal>
      )}
    </>
  );
};
